import HasPermission, { PermissionAuth } from '@/components/HasPermission';
import { InitComponentStateType, PersistComponentOptions, createPersistentComponentState } from '@/state';

import { customLocalStorage } from '@/state/storage';

// TODO: Replace with AirlineTripsColumnEnum defined by back-end instead of hardcoding
export enum AirlineColumnKeyEnum {
  FLAGS = 'flags',
  TYPE = 'type',
  SCHEDULED_DATE = 'scheduledDate',
  SCHEDULED_TIME = 'scheduledTime',
  ACTUAL = 'actual',
  CITY = 'city',
  AIRPORT_CODE = 'airportCode',
  IATA_CODE = 'iataCode',
  FLIGHT_NUMBER = 'flightNumber',
  PILOT = 'pilot',
  ATTENDANT = 'attendant',
  DRIVER = 'driver',
  VEHICLE = 'vehicle',
  PU_LOCATION = 'puLocation',
  DO_LOCATION = 'doLocation',
  CLIENT = 'client',
  COMPLETION = 'completion',
  ACTIVITY = 'activity',
  FCR = 'fcr',
  COMBINE = 'combine',
  RATE = 'rate',
  ETA = 'eta',
}
export type AirlineTripSettingsState = {
  show: boolean;
  remember: boolean;
  columns: Map<AirlineColumnKeyEnum, boolean>;
  onToggleColumn: (key: AirlineColumnKeyEnum) => void;
  setColumns: () => void;
};
const AIRLINE_COLUMN_PERMISSION_MAPPING: Partial<Record<AirlineColumnKeyEnum, keyof PermissionAuth>> = {
  [AirlineColumnKeyEnum.RATE]: 'allowSearchTripsByRate',
};
const initAirlineTripSettingsState: InitComponentStateType<AirlineTripSettingsState> = (set) => ({
  show: false,
  remember: false,
  columns: new Map(),
  onToggleColumn: (key: AirlineColumnKeyEnum): void => {
    set((current: AirlineTripSettingsState): AirlineTripSettingsState => {
      const value = current.columns.get(key);
      if (value === undefined) return current; // tried to set a column that should be hidden
      // otherwise toggle the column
      const columns = new Map(current.columns);
      columns.set(key, !value);
      return { ...current, columns };
    });
  },
  setColumns: (): void => {
    set((current: AirlineTripSettingsState): AirlineTripSettingsState => {
      // convert the set to map
      const airlineColumns = Object.entries(AirlineColumnKeyEnum);
      const columns = new Map();
      const currentColumns = new Map(current.columns);
      airlineColumns.forEach(([key, column]: [string, AirlineColumnKeyEnum]): void => {
        const checkPermission = AIRLINE_COLUMN_PERMISSION_MAPPING[column];
        if (checkPermission && !HasPermission.check(checkPermission)) return;
        if (!HasPermission.checkUserConfig('/airline-trips', 'airlineTrips', key)) return;
        // if remember is true and the column is already in the map
        if (current.remember && currentColumns.has(column)) {
          columns.set(column, currentColumns.get(column));
          return;
        }
        columns.set(column, true);
      });
      return { ...current, columns };
    });
  },
});

const persistComponentOptions: PersistComponentOptions<AirlineTripSettingsState> = {
  live: false,
  storage: customLocalStorage,
  reviver: (key: string, value: any): any => {
    if (key === 'columns') {
      if (Array.isArray(value)) return new Map(value);
      if (typeof value === 'object') return new Map(Object.entries(value));
    }
    return value;
  },
  replacer: (key: string, value: any): any => {
    if (key === 'columns') return Array.from(value.entries());
    return value;
  },
};

export const AIRLINE_TRIPS_SETTINGS_STORAGE_KEY = 'airlineTripSettings';
const useAirlineTripSettings = createPersistentComponentState(
  AIRLINE_TRIPS_SETTINGS_STORAGE_KEY,
  initAirlineTripSettingsState,
  persistComponentOptions
);

export default useAirlineTripSettings;
